import { Card, CardContent, cn } from "@devhop/ui";
import type { LucideIcon } from "lucide-react";

type FormsDashboardStatCardProps = {
  title: string;
  value: number;
  isLoading: boolean;
  icon: LucideIcon;
  tone?: "default" | "draft" | "published" | "closed";
  hint?: string;
  onClick?: () => void;
};

const toneStyles = {
  default: {
    icon: "bg-primary/10 text-primary",
    hover: "hover:border-primary/30 hover:bg-primary/5",
  },
  draft: {
    icon: "bg-muted text-muted-foreground",
    hover: "hover:border-border hover:bg-muted/40",
  },
  published: {
    icon: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
    hover: "hover:border-emerald-500/30 hover:bg-emerald-500/5",
  },
  closed: {
    icon: "bg-orange-500/10 text-orange-700 dark:text-orange-400",
    hover: "hover:border-orange-500/30 hover:bg-orange-500/5",
  },
} as const;

export function FormsDashboardStatCard({
  title,
  value,
  isLoading,
  icon: Icon,
  tone = "default",
  hint,
  onClick,
}: FormsDashboardStatCardProps) {
  const styles = toneStyles[tone];
  const interactive = !!onClick;

  return (
    <Card
      className={cn(
        "transition-colors",
        interactive && "cursor-pointer",
        interactive && styles.hover,
      )}
      onClick={onClick}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
    >
      <CardContent className="flex items-start justify-between gap-4">
        <div className="min-w-0 space-y-2">
          <p className="text-muted-foreground text-sm">{title}</p>
          <p className="font-semibold text-3xl tabular-nums tracking-tight">
            {isLoading ? "—" : value}
          </p>
          {hint ? (
            <p className="text-muted-foreground text-xs">{hint}</p>
          ) : null}
        </div>
        <div
          className={cn(
            "flex size-11 shrink-0 items-center justify-center rounded-xl",
            styles.icon,
          )}
        >
          <Icon className="size-5" aria-hidden />
        </div>
      </CardContent>
    </Card>
  );
}
